import { useEffect, useState, useCallback } from 'react';

const API_BASE = '/api/v1/admin/social';

export interface UseAdminSocialState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

function getAuthHeader(): Record<string, string> {
  try {
    const token = localStorage.getItem('asklaw_access_token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

export function useAdminSocial<T>(path: string): UseAdminSocialState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}${path}`, { headers: getAuthHeader() });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
      }
      const json = (await res.json()) as T;
      setData(json);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [path]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export async function postRefresh(
  scope: 'all' | 'post' | 'page' = 'all',
): Promise<{ fetched: number; errors: unknown[] }> {
  const res = await fetch(`${API_BASE}/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
    body: JSON.stringify({ scope }),
  });
  if (!res.ok) throw new Error(`refresh_fail_${res.status}`);
  return res.json() as Promise<{ fetched: number; errors: unknown[] }>;
}
